import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import Footer from '@/components/Footer';
import { useLocation } from 'wouter';
import logoImage from "@assets/logo-e-zez-png-3.png";
import Lightbox from '@/components/Lightbox';

// Type definition for gallery images
interface GalleryImage {
  id: number;
  src: string;
  alt: string;
  title?: string;
  description?: string;
  location?: string;
  category: string;
  tags: string[];
  uploadedBy?: string;
  uploadedAt?: string;
}

export default function Gallery() {
  const [, navigate] = useLocation();
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([]);
  const [filteredImages, setFilteredImages] = useState<GalleryImage[]>([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // State for lightbox
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // Functions to handle lightbox
  const openLightbox = (image: GalleryImage) => {
    setSelectedImage(image);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    // Clear selected image after animation completes
    setTimeout(() => setSelectedImage(null), 300);
  };

  // Function to shuffle array randomly
  const shuffleArray = <T,>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Fetch gallery images from API
  const fetchGalleryImages = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch('/api/gallery');
      if (!response.ok) {
        throw new Error('Failed to fetch gallery images');
      }

      const data = await response.json();
      if (data.success) {
        // Shuffle the images randomly every time they're fetched
        const shuffledImages = shuffleArray(data.data || []);
        setGalleryImages(shuffledImages);
      } else {
        throw new Error(data.message || 'Failed to load gallery images');
      }
    } catch (error) {
      console.error('Error fetching gallery images:', error);
      setError('Failed to load gallery images. Please try again later.');
      setGalleryImages([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Load gallery images on component mount
  useEffect(() => {
    fetchGalleryImages();
  }, []);

  // Get all unique categories from the images
  const categories = ['all', ...Array.from(new Set(galleryImages.map(img => img.category)))];

  // Filter images based on category and search term
  useEffect(() => {
    const filtered = galleryImages.filter(img => {
      const matchesCategory = activeCategory === 'all' || img.category === activeCategory;
      const matchesSearch = img.alt.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (img.title && img.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
                           (img.description && img.description.toLowerCase().includes(searchTerm.toLowerCase()));
      return matchesCategory && matchesSearch;
    });
    setFilteredImages(filtered);
  }, [galleryImages, activeCategory, searchTerm]);

  // Refresh gallery images every 30 seconds to show new uploads
  useEffect(() => {
    const interval = setInterval(fetchGalleryImages, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Gallery Header - White background with black text */}
      <div className="bg-white text-black py-4 sticky top-0 z-10 shadow-md">
        <div className="container mx-auto px-4">
          {/* Logo and Navigation Links in the same row */}
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <img src={logoImage} alt="Swiss Welding" className="h-12 w-auto" />
            </div>

            {/* Navigation Links */}
            <div className="hidden md:flex items-center space-x-8">
              <button 
                onClick={() => navigate('/')} 
                className="text-black hover:text-red-500 hover:underline transition-colors font-medium"
              >
                HOME
              </button>
              <button 
                onClick={() => navigate('/#about')} 
                className="text-black hover:text-red-500 hover:underline transition-colors font-medium"
              >
                ABOUT
              </button>
              <button 
                onClick={() => navigate('/#services')} 
                className="text-black hover:text-red-500 hover:underline transition-colors font-medium"
              >
                SERVICES
              </button>
              <button 
                className="text-red-500 font-medium border-b-2 border-red-500"
              >
                GALLERY
              </button>
              <button 
                onClick={() => navigate('/#testimonials')} 
                className="text-black hover:text-red-500 hover:underline transition-colors font-medium"
              >
                TESTIMONIALS
              </button>
              <button 
                onClick={() => navigate('/#timeline')} 
                className="text-black hover:text-red-500 hover:underline transition-colors font-medium"
              >
                TIMELINE
              </button>
              <button 
                onClick={() => navigate('/#estimation')} 
                className="text-black hover:text-red-500 hover:underline transition-colors font-medium"
              >
                ESTIMATE
              </button>
              <button 
                onClick={() => navigate('/#booking')} 
                className="text-black hover:text-red-500 hover:underline transition-colors font-medium"
              >
                BOOK NOW
              </button>
              <button 
                onClick={() => navigate('/#contact')} 
                className="text-black hover:text-red-500 hover:underline transition-colors font-medium"
              >
                CONTACT
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Banner */}
      <div className="relative h-80 bg-gradient-to-r from-black to-red-900 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://www.swissweldingllc.com/wp-content/uploads/2020/07/105287110_2670184096585514_4836462035795912567_o.jpg" 
            alt="Gallery Background" 
            className="w-full h-full object-cover object-center"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent"></div>
        <div className="container mx-auto px-4 h-full flex flex-col justify-center items-center relative z-10 text-white text-center">
          <motion.h1 
            className="text-4xl md:text-5xl text-white font-bold mb-4"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Our Project Portfolio
          </motion.h1>
          <motion.div 
            className="w-24 h-1 bg-red-600 mb-6"
            initial={{ width: 0 }}
            animate={{ width: 96 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          ></motion.div>
          <motion.p 
            className="text-lg md:text-xl max-w-3xl text-gray-200"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.7 }}
          >
            Browse through our collection of completed projects showcasing our precision and craftsmanship in welding.
          </motion.p>
        </div>
      </div>

      {/* Gallery Content */}
      <div className="flex-grow py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="mb-12">
            <motion.div 
              className="bg-white p-8 rounded-xl shadow-lg -mt-20 relative z-20 border-t-4 border-red-600"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ type: "spring", stiffness: 100, damping: 15 }}
            >
              <div className="flex flex-col md:flex-row gap-6 items-center">
                <div className="w-full md:w-1/3">
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">Find Your Project</h3>
                  <p className="text-gray-600 mb-4">Filter by category or search by keyword to explore our work.</p>
                  <div className="relative">
                    <Input
                      type="search"
                      placeholder="Search projects by keyword..."
                      className="w-full pr-10 border-gray-300 focus:border-red-500 focus:ring-red-500"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      className="h-5 w-5 absolute right-3 top-3 text-gray-400" 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <div className="mt-4">
                    <Button
                      onClick={fetchGalleryImages}
                      variant="outline"
                      size="sm"
                      className="text-red-600 border-red-600 hover:bg-red-600 hover:text-white"
                    >
                      Refresh Gallery
                    </Button>
                  </div>
                </div>
                <div className="w-full md:w-2/3">
                  <div className="flex gap-2 flex-wrap justify-start">
                    {categories.map(category => (
                      <Button
                        key={category}
                        variant={activeCategory === category ? "default" : "outline"}
                        onClick={() => setActiveCategory(category)}
                        className={`capitalize ${activeCategory === category ? 'bg-red-600 hover:bg-red-600/90 shadow-md' : 'hover:border-red-600 hover:text-red-600'}`}
                      >
                        {category === 'all' ? 'All Projects' : 
                         category.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="text-center py-16">
              <div className="animate-spin h-12 w-12 border-4 border-red-600 border-t-transparent rounded-full inline-block mb-4"></div>
              <p className="text-xl text-gray-600">Loading gallery images...</p>
            </div>
          )}

          {/* Error State */}
          {error && !isLoading && (
            <div className="text-center py-16 bg-white/50 backdrop-blur-sm rounded-lg shadow-sm">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-red-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <p className="text-xl text-red-600 mb-4">{error}</p>
              <Button 
                onClick={fetchGalleryImages}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Try Again
              </Button>
            </div>
          )}

          {/* Gallery Grid */}
          {!isLoading && !error && (
            <motion.div 
              className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 md:gap-8"
              layout
              variants={{
                hidden: { opacity: 0 },
                show: {
                  opacity: 1,
                  transition: {
                    staggerChildren: 0.1
                  }
                }
              }}
              initial="hidden"
              animate="show"
            >
              <AnimatePresence>
                {filteredImages.length > 0 ? (
              (() => {
                // Group images by projectId for display
                const groupedImages = filteredImages.reduce((acc, image) => {
                  const key = image.projectId || `single-${image.id}`;
                  if (!acc[key]) {
                    acc[key] = [];
                  }
                  acc[key].push(image);
                  return acc;
                }, {} as Record<string, typeof filteredImages>);

                return Object.entries(groupedImages).map(([projectKey, images]) => {
                  const primaryImage = images.find(img => img.isPrimary) || images[0];
                  const imageCount = images.length;
                  const isProject = !!primaryImage.projectId && imageCount > 1;

                  return (
                    <motion.div
                      key={projectKey}
                      className="group relative aspect-[4/3] rounded-lg overflow-hidden bg-white shadow-lg hover:shadow-xl transition-shadow cursor-pointer"
                      onClick={() => openLightbox(primaryImage)}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5 }}
                      whileHover={{ y: -5 }}
                      style={{ minHeight: '280px' }}
                    >
                      <img
                                src={primaryImage.src}
                                alt={primaryImage.alt}
                                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                                onError={(e) => {
                                console.error('Failed to load image:', primaryImage.src);
                                const target = e.currentTarget;
                                
                                // Set placeholder for failed images
                                target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iMzAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yMDAgMTUwTDE1MCAyMDBIMjUwTDIwMCAxNTBaIiBmaWxsPSIjRDFEOUUwIi8+CjxjaXJjbGUgY3g9IjE3MCIgY3k9IjEyMCIgcj0iMjAiIGZpbGw9IiNEMUQ5RTAiLz4KPHRleHQgeD0iMjAwIiB5PSIyNjAiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzY3NzI4MCIgdGV4dC1hbmNob3I9Im1pZGRsZSI+SW1hZ2UgTm90IEZvdW5kPC90ZXh0Pgo8L3N2Zz4K';
                              }}
                            />

                      {/* Project indicator */}
                      {isProject && (
                        <div className="absolute top-3 right-3 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
                          {imageCount} images
                        </div>
                      )}

                      {/* Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="absolute bottom-4 left-4 right-4 text-white">
                          <h3 className="text-lg text-white font-semibold mb-1">
                            {primaryImage.title || primaryImage.alt}
                          </h3>
                          {primaryImage.location && (
                            <p className="text-sm text-gray-300 mb-2">
                              📍 {primaryImage.location}
                            </p>
                          )}
                          <p className="text-gray-300 text-sm line-clamp-2 mb-2">
                            {primaryImage.description || `Professional ${primaryImage.alt} project showcasing our craftsmanship and attention to detail.`}
                          </p>
                          {primaryImage.tags && primaryImage.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {primaryImage.tags.slice(0, 3).map((tag, tagIndex) => (
                                <span 
                                  key={tagIndex}
                                  className="text-xs bg-white/20 text-white px-2 py-1 rounded-full backdrop-blur-sm"
                                >
                                  {tag}
                                </span>
                              ))}
                              {primaryImage.tags.length > 3 && (
                                <span className="text-xs text-gray-300">
                                  +{primaryImage.tags.length - 3} more
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                });
              })()
            ) : (
                  <motion.div 
                    className="col-span-3 text-center py-16 bg-white/50 backdrop-blur-sm rounded-lg shadow-sm"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-xl text-gray-600 mb-4">
                      {galleryImages.length === 0 
                        ? "No gallery images uploaded yet. Use the admin panel to add some projects!"
                        : "No projects found matching your criteria."
                      }
                    </p>
                    <Button 
                      onClick={() => {setActiveCategory('all'); setSearchTerm('')}} 
                      className="mt-2 bg-red-600 hover:bg-red-700 text-white py-2 px-6"
                    >
                      {galleryImages.length === 0 ? "Refresh Gallery" : "View All Projects"}
                    </Button>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}

          <motion.div 
            className="flex justify-center mt-16 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.5 }}
          >
            <Button 
              variant="outline" 
              onClick={() => navigate('/')} 
              className="px-8 py-4 border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-white transition-all duration-300 text-lg font-medium rounded-full shadow-md group flex items-center gap-2"
            >
              <span>Return to Homepage</span>
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-5 w-5 transform group-hover:translate-x-1 transition-transform" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </Button>
          </motion.div>
        </div>
      </div>

      {/* Floating Back to Home Button */}
      <motion.div 
        className="fixed bottom-8 right-8 z-10"
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ 
          type: "spring",
          stiffness: 260,
          damping: 20,
          delay: 1.5
        }}
        whileHover={{ 
          scale: 1.1,
          transition: { duration: 0.2 }
        }}
        whileTap={{ scale: 0.9 }}
      >
        <Button
          onClick={() => navigate('/')}
          className="bg-red-600 text-white hover:bg-red-700 shadow-xl rounded-full p-4 h-14 w-14 flex items-center justify-center relative overflow-hidden group"
        >
          <motion.div 
            className="absolute inset-0 bg-black"
            initial={{ scale: 0, opacity: 0 }}
            whileHover={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.3 }}
          />
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="24" 
            height="24" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
            className="relative z-10"
          >
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
            <polyline points="9 22 9 12 15 12 15 22"></polyline>
          </svg>
        </Button>
      </motion.div>

      {/* Footer */}
      <div className="bg-black text-white py-6 text-center">
        <p>© {new Date().getFullYear()} Swiss Welding LLC. All rights reserved.</p>
      </div>

      {/* Lightbox for project details */}
      <Lightbox 
        isOpen={lightboxOpen}
        onClose={closeLightbox}
        image={selectedImage}
      />
    </div>
  );
}